import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth, useCan } from '../contexts/AuthContext';
import { useImportantNotes } from '../contexts/ImportantNotesContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ChevronLeft, ChevronRight, Plus, Clock, MapPin,
    User, CalendarDays, LayoutList, Columns3, Mail, List
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { 
    format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    addDays, addMonths, subMonths, addWeeks, subWeeks,
    isSameMonth, isSameDay, isToday, parseISO, getDay
} from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
    { value: 'visite_presentiel', label: 'Visite présentiel', color: 'bg-[#1B365D]', text: 'text-[#1B365D]', light: 'bg-blue-50 border-[#1B365D]/30' },
    { value: 'visite_virtuelle', label: 'Visite virtuelle', color: 'bg-[#C9A962]', text: 'text-[#C9A962]', light: 'bg-amber-50 border-[#C9A962]/30' },
];

const VISIT_STATUSES = [
    { value: 'planifiee', label: 'Planifiée', color: 'bg-blue-100 text-blue-700' },
    { value: 'commencee', label: 'Commencée', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'terminee', label: 'Terminée', color: 'bg-green-100 text-green-700' },
    { value: 'annulee', label: 'Annulée', color: 'bg-red-100 text-red-700' },
    { value: 'reportee', label: 'Reportée', color: 'bg-orange-100 text-orange-700' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
    const h = i.toString().padStart(2, '0');
    return [{ value: `${h}:00`, label: `${h}:00` }, { value: `${h}:30`, label: `${h}:30` }];
}).flat();

// Add `addMin` minutes to an `HH:MM` string. Wraps to 23:30 if overflow.
function addMinutes(time, addMin) {
    if (!time || !time.includes(':')) return time;
    const [hh, mm] = time.split(':').map(Number);
    const total = hh * 60 + mm + addMin;
    if (total >= 24 * 60) return '23:30';
    const nh = Math.floor(total / 60).toString().padStart(2, '0');
    const nm = (total % 60).toString().padStart(2, '0');
    return `${nh}:${nm}`;
}

// Today's date YYYY-MM-DD (used for the Date input min attribute)
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const VIEWS = [
    { value: 'day', label: 'Jour', icon: LayoutList },
    { value: 'week', label: 'Semaine', icon: Columns3 },
    { value: 'month', label: 'Mois', icon: CalendarDays },
    { value: 'list', label: 'Liste', icon: List },
];

const CAN_ASSIGN = ['admin', 'assistante', 'responsable'];
const getEventTypeConfig = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];
const getVisitStatusConfig = (s) => VISIT_STATUSES.find(v => v.value === s) || VISIT_STATUSES[0];

const emptyForm = {
    title: '', type: 'visite_presentiel', date: '',
    start_time: '09:00', end_time: '10:00',
    location: '', description: '', assigned_to: '', prospect_id: ''
};

export default function Agenda() {
    const { user } = useAuth();
    const { canWrite: canWriteAgenda } = useCan('agenda');
    const { flags: importantFlags } = useImportantNotes();
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState('month');
    const [events, setEvents] = useState([]);
    const [users, setUsers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [showEventModal, setShowEventModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [eventForm, setEventForm] = useState({ ...emptyForm });
    const [sendConfirmEmail, setSendConfirmEmail] = useState(false);
    const [prospectEmail, setProspectEmail] = useState('');
    const { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation } = useUnsavedChanges();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const canAssign = CAN_ASSIGN.includes(user?.role);

    const getDateRange = useCallback(() => {
        if (view === 'day') { const d = format(currentDate, 'yyyy-MM-dd'); return { start: d, end: d }; }
        if (view === 'week') {
            return { start: format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
        }
        const ms = startOfMonth(currentDate);
        return { start: format(startOfWeek(ms, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(endOfMonth(ms), { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    }, [currentDate, view]);

    const fetchEvents = useCallback(async () => {
        try {
            const { start, end } = getDateRange();
            const res = await axios.get(`${API_URL}/api/events?start=${start}&end=${end}`, { withCredentials: true });
            setEvents(res.data);
        } catch (err) { console.error(err); }
    }, [getDateRange]);

    const fetchUsers = useCallback(async () => {
        if (!canAssign) return;
        try { const res = await axios.get(`${API_URL}/api/users/list`, { withCredentials: true }); setUsers(res.data); }
        catch (err) { console.error(err); }
    }, [canAssign]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);
    useEffect(() => { fetchUsers(); }, [fetchUsers]);
    useEffect(() => {
        axios.get(`${API_URL}/api/prospects`, { withCredentials: true })
            .then(r => setProspects(r.data)).catch(() => {});
    }, []);

    const nav = (dir) => {
        const fn = dir === 'prev'
            ? (view === 'month' ? subMonths : view === 'week' ? subWeeks : (d) => addDays(d, -1))
            : (view === 'month' ? addMonths : view === 'week' ? addWeeks : (d) => addDays(d, 1));
        setCurrentDate(fn(currentDate, 1));
    };

    const openCreate = (day, startH) => {
        const f = { ...emptyForm, date: format(day || new Date(), 'yyyy-MM-dd') };
        if (startH !== undefined) {
            f.start_time = `${startH.toString().padStart(2, '0')}:00`;
            f.end_time = `${(startH + 1).toString().padStart(2, '0')}:00`;
        }
        setEventForm(f);
        setHasChanges(false);
        setSendConfirmEmail(false);
        setProspectEmail('');
        setShowEventModal(true);
    };

    const autoEndTime = (start) => {
        const [h, m] = start.split(':').map(Number);
        const endH = Math.min(h + 1, 23);
        return `${endH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const updateEventForm = (updates) => {
        if (updates.start_time) {
            updates.end_time = autoEndTime(updates.start_time);
        }
        setEventForm(prev => ({ ...prev, ...updates }));
        setHasChanges(true);
    };

    const closeEventModal = () => {
        setShowEventModal(false);
        setHasChanges(false);
    };

    const openDetail = (event, e) => { if (e) e.stopPropagation(); navigate(`/agenda/${event.id}`); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!eventForm.title.trim()) { toast.error('Le titre est requis'); return; }
        setSubmitting(true);
        try {
            const payload = { ...eventForm };
            const shouldSendEmail = sendConfirmEmail && prospectEmail;
            if (!payload.assigned_to) delete payload.assigned_to;
            if (!payload.prospect_id) delete payload.prospect_id;
            const res = await axios.post(`${API_URL}/api/events`, payload, { withCredentials: true });
            toast.success('Événement créé');

            // Send confirmation email if checkbox was checked
            if (shouldSendEmail && res.data?.prospect?.email) {
                try {
                    const prospect = res.data.prospect;
                    await axios.post(`${API_URL}/api/emails/send-rdv-confirmation`, {
                        to_email: prospect.email,
                        client_name: prospect.nom,
                        date: eventForm.date,
                        heure: eventForm.start_time,
                        visit_type: eventForm.type,
                        address: eventForm.location || prospect.adresse_depart?.adresse || '',
                        event_id: res.data.id,
                        prospect_id: eventForm.prospect_id,
                    }, { withCredentials: true });
                    toast.success('Le mail est bien envoyé merci 😎');
                } catch (emailErr) {
                    const msg = emailErr.response?.data?.detail || "Erreur lors de l'envoi de l'email";
                    toast.error(msg);
                }
            }

            setHasChanges(false);
            setShowEventModal(false);
            setSendConfirmEmail(false);
            setProspectEmail('');
            fetchEvents();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'object' && detail?.code === 'active_visit_exists') {
                toast.error(detail.message || 'Ce prospect a déjà une visite active. Reprogrammez-la depuis sa fiche.');
            } else {
                toast.error('Erreur lors de la création');
            }
        }
        finally { setSubmitting(false); }
    };

    const getEventsForDay = (day) => events.filter(ev => isSameDay(parseISO(ev.date), day));

    const viewTitle = () => {
        if (view === 'day') return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
        if (view === 'week') {
            const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
            return `${format(ws, 'd MMM', { locale: fr })} — ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: fr })}`;
        }
        return format(currentDate, 'MMMM yyyy', { locale: fr });
    };

    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const workHours = Array.from({ length: 14 }, (_, i) => i + 7);

    /* =================== EVENT PILL =================== */
    const renderEventPill = (event, compact = false) => {
        const tc = getEventTypeConfig(event.type);
        const vs = event.visit_status;
        const cancelled = vs === 'annulee';
        const postponed = vs === 'reportee';

        let pillColor = tc.color;
        if (cancelled) pillColor = 'bg-red-500';
        else if (postponed) pillColor = 'bg-orange-500';

        const hasImportant = !!event.prospect_id && (importantFlags[event.prospect_id] || 0) > 0;

        return (
            <div
                key={event.id}
                onClick={(e) => openDetail(event, e)}
                data-testid={`event-pill-${event.id}`}
                className={`${pillColor} text-white rounded-md px-1.5 py-[3px] cursor-pointer hover:opacity-90 hover:scale-[1.02] transition-all truncate ${compact ? 'text-[10px] leading-tight' : 'text-xs'} ${cancelled ? 'line-through' : ''} ${hasImportant ? 'ring-2 ring-red-500 ring-offset-1' : ''} shadow-sm`}
                title={`${event.start_time || ''} ${event.prospect ? event.prospect.nom : event.title}${cancelled ? ' (Annulée)' : postponed ? ' (Reportée)' : ''}${hasImportant ? ' — Note importante' : ''}`}
            >
                {event.start_time && <span className="font-bold mr-1">{event.start_time}</span>}
                {event.prospect ? event.prospect.nom : event.title}
            </div>
        );
    };

    /* =================== MONTH VIEW =================== */
    const renderMonthView = () => {
        const monthStart = startOfMonth(currentDate);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });
        const days = [];
        let d = gridStart;
        while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }

        return (
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                    {weekDays.map((wd, i) => (
                        <div key={wd} className={`py-2 md:py-3 text-center text-xs md:text-sm font-semibold ${i >= 5 ? 'text-[#C9A962]' : 'text-[#1B365D]'}`}>
                            <span className="hidden sm:inline">{wd}</span><span className="sm:hidden">{wd.charAt(0)}</span>
                        </div>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-7" style={{ gridAutoRows: 'minmax(80px, auto)' }}>
                        {days.map((day, idx) => {
                            const dayEvents = getEventsForDay(day);
                            const inMonth = isSameMonth(day, currentDate);
                            const today = isToday(day);
                            const dow = getDay(day);
                            const isWeekend = dow === 0 || dow === 6;
                            return (
                                <div key={idx} onClick={() => openCreate(day)}
                                    className={`p-1 md:p-1.5 border-b border-r border-gray-100 cursor-pointer transition-colors hover:bg-blue-50/40 ${!inMonth ? 'bg-gray-50/50' : ''} ${isWeekend && inMonth ? 'bg-[#C9A962]/5' : ''}`}>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 md:w-7 md:h-7 text-[10px] md:text-sm rounded-full mb-0.5 ${today ? 'bg-[#1B365D] text-white font-bold' : inMonth ? 'text-[#1B365D] font-medium' : 'text-gray-400'}`}>
                                        {format(day, 'd')}
                                    </span>
                                    <div className="space-y-[3px]">
                                        {dayEvents
                                            .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                                            .map(ev => renderEventPill(ev, true))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    /* =================== WEEK VIEW =================== */
    const renderWeekView = () => {
        const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
        const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
        return (
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}>
                    <div className="py-2 border-r border-gray-200" />
                    {days.map((day, i) => (
                        <div key={i} className={`py-2 text-center border-r border-gray-100 ${isToday(day) ? 'bg-[#1B365D]/5' : ''}`}>
                            <div className={`text-[10px] md:text-xs font-medium ${i >= 5 ? 'text-[#C9A962]' : 'text-gray-500'}`}>
                                <span className="hidden sm:inline">{weekDays[i]}</span><span className="sm:hidden">{weekDays[i].charAt(0)}</span>
                            </div>
                            <div className={`inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full text-xs md:text-base font-semibold ${isToday(day) ? 'bg-[#1B365D] text-white' : 'text-[#1B365D]'}`}>
                                {format(day, 'd')}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                    {workHours.map(hour => (
                        <div key={hour} className="grid border-b border-gray-100" style={{ gridTemplateColumns: '50px repeat(7, 1fr)', minHeight: '60px' }}>
                            <div className="text-[10px] md:text-xs text-gray-400 text-right pr-2 pt-1 border-r border-gray-200">{hour.toString().padStart(2, '0')}:00</div>
                            {days.map((day, i) => {
                                const hourEvents = getEventsForDay(day).filter(ev => parseInt(ev.start_time?.split(':')[0] || '0', 10) === hour);
                                return (
                                    <div key={i} onClick={() => openCreate(day, hour)}
                                        className={`border-r border-gray-100 p-0.5 cursor-pointer hover:bg-blue-50/30 ${isToday(day) ? 'bg-[#1B365D]/[0.02]' : ''}`}>
                                        {hourEvents.map(ev => renderEventPill(ev))}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /* =================== DAY VIEW =================== */
    const renderDayView = () => {
        const dayEvents = getEventsForDay(currentDate);
        const dayHours = Array.from({ length: 13 }, (_, i) => i + 8); // 8h -> 20h

        return (
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                {/* Day header */}
                <div className="py-3 px-5 border-b border-gray-200 bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/5 flex items-center justify-between">
                    <div>
                        <div className={`text-lg md:text-xl font-bold capitalize ${isToday(currentDate) ? 'text-[#1B365D]' : 'text-gray-700'}`}>
                            {format(currentDate, 'EEEE d MMMM', { locale: fr })}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{dayEvents.length} rendez-vous</div>
                    </div>
                    {isToday(currentDate) && <Badge className="bg-[#C9A962] text-white text-xs">Aujourd'hui</Badge>}
                </div>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                    {dayHours.map(hour => {
                        const hStr = hour.toString().padStart(2, '0');
                        const hourEvents = dayEvents.filter(ev => parseInt(ev.start_time?.split(':')[0] || '0', 10) === hour);
                        const hasEvents = hourEvents.length > 0;

                        return (
                            <div key={hour}
                                className={`flex border-b border-gray-100 transition-colors cursor-pointer ${hasEvents ? 'bg-[#1B365D]/[0.02]' : 'hover:bg-blue-50/30'}`}
                                style={{ minHeight: hasEvents ? `${Math.max(60, hourEvents.length * 72)}px` : '50px' }}
                                onClick={() => openCreate(currentDate, hour)}>
                                {/* Hour label */}
                                <div className="w-16 md:w-20 shrink-0 border-r border-gray-200 flex flex-col items-end pr-3 pt-2">
                                    <span className={`text-xs md:text-sm font-medium ${hasEvents ? 'text-[#1B365D]' : 'text-gray-400'}`}>{hStr}:00</span>
                                </div>
                                {/* Events column */}
                                <div className="flex-1 p-1.5 space-y-1.5">
                                    {hourEvents.map(ev => {
                                        const tc = getEventTypeConfig(ev.type);
                                        const cancelled = ev.visit_status === 'annulee';
                                        const postponed = ev.visit_status === 'reportee';
                                        const statusCfg = getVisitStatusConfig(ev.visit_status);

                                        let cardBorder = ev.type === 'visite_presentiel' ? 'border-l-[#1B365D] bg-[#1B365D]/5' : 'border-l-[#C9A962] bg-[#C9A962]/5';
                                        if (cancelled) cardBorder = 'border-l-red-500 bg-red-50';
                                        else if (postponed) cardBorder = 'border-l-orange-500 bg-orange-50';

                                        return (
                                            <div key={ev.id} onClick={(e) => openDetail(ev, e)}
                                                className={`rounded-lg p-3 cursor-pointer hover:shadow-md transition-all border-l-4 ${cardBorder}`}>
                                                <div className="flex items-center gap-2.5">
                                                    <div className={`w-3 h-3 rounded-full ${tc.color} shrink-0 shadow-sm`} />
                                                    <span className={`font-semibold text-sm text-[#1B365D] truncate ${cancelled ? 'line-through' : ''}`}>
                                                        {ev.prospect ? ev.prospect.nom : ev.title}
                                                    </span>
                                                    {ev.visit_status && <Badge className={`${statusCfg.color} text-[10px] px-2 py-0.5 ml-auto shrink-0`}>{statusCfg.label}</Badge>}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1 font-medium text-[#1B365D]/70">
                                                        <Clock className="w-3 h-3" />{ev.start_time} - {ev.end_time}
                                                    </span>
                                                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#C9A962]" />{ev.location}</span>}
                                                    {ev.assigned_to_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{ev.assigned_to_name}</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    /* =================== MOBILE SCHEDULE VIEW =================== */
    const [mobileEvents, setMobileEvents] = useState([]);
    const [mobileRange, setMobileRange] = useState(() => {
        const t = new Date(); t.setHours(0, 0, 0, 0);
        return { start: addDays(t, -30), end: addDays(t, 30) };
    });
    const mobileScrollRef = useRef(null);
    const todayRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const fetchMobileEvents = useCallback(async (rangeStart, rangeEnd) => {
        try {
            const s = format(rangeStart, 'yyyy-MM-dd');
            const e = format(rangeEnd, 'yyyy-MM-dd');
            const res = await axios.get(`${API_URL}/api/events?start=${s}&end=${e}`, { withCredentials: true });
            return res.data;
        } catch { return []; }
    }, []);

    useEffect(() => {
        if (!isMobile && view !== 'list') return;
        fetchMobileEvents(mobileRange.start, mobileRange.end).then(setMobileEvents);
    }, [isMobile, view, mobileRange, fetchMobileEvents]);

    // Scroll to today on first load (centered so past days are visible above)
    useEffect(() => {
        if ((isMobile || view === 'list') && todayRef.current) {
            setTimeout(() => todayRef.current?.scrollIntoView({ block: 'center' }), 100);
        }
    }, [isMobile, view, mobileEvents]);

    const handleMobileScroll = useCallback(() => {
        const el = mobileScrollRef.current;
        if (!el || loadingMoreRef.current) return;
        // Load future days
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            loadingMoreRef.current = true;
            const newEnd = addDays(mobileRange.end, 30);
            setMobileRange(prev => ({ ...prev, end: newEnd }));
            setTimeout(() => { loadingMoreRef.current = false; }, 500);
        }
        // Load past days
        if (el.scrollTop < 200) {
            loadingMoreRef.current = true;
            const prevScrollHeight = el.scrollHeight;
            const newStart = addDays(mobileRange.start, -30);
            setMobileRange(prev => ({ ...prev, start: newStart }));
            setTimeout(() => {
                // Maintain scroll position after prepending days
                const diff = el.scrollHeight - prevScrollHeight;
                el.scrollTop += diff;
                loadingMoreRef.current = false;
            }, 500);
        }
    }, [mobileRange]);

    const getMobileEventsForDay = useCallback((day) => {
        return mobileEvents.filter(ev => isSameDay(parseISO(ev.date), day));
    }, [mobileEvents]);

    const renderMobileScheduleView = () => {
        const allDays = [];
        let d = mobileRange.start;
        while (d <= mobileRange.end) { allDays.push(d); d = addDays(d, 1); }

        const EVENT_COLORS = [
            'bg-blue-400', 'bg-purple-400', 'bg-pink-400', 'bg-green-400',
            'bg-amber-400', 'bg-cyan-400', 'bg-red-400', 'bg-indigo-400',
        ];
        const getEventColor = (ev) => {
            if (ev.visit_status === 'annulee') return 'bg-red-400';
            if (ev.visit_status === 'reportee') return 'bg-orange-400';
            if (ev.type === 'visite_presentiel') return 'bg-blue-400';
            if (ev.type === 'visite_virtuelle') return 'bg-purple-400';
            const hash = (ev.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
            return EVENT_COLORS[hash % EVENT_COLORS.length];
        };

        const dayLabels = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
        let lastMonth = null;

        return (
            <div ref={mobileScrollRef} onScroll={handleMobileScroll}
                className="flex-1 overflow-y-auto -mx-4 px-1" data-testid="mobile-schedule">
                {allDays.map((day, idx) => {
                    const dayEvents = getMobileEventsForDay(day).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
                    const todayDay = isToday(day);
                    const dayOfWeek = dayLabels[getDay(day)];
                    const monthLabel = format(day, 'MMMM yyyy', { locale: fr });
                    const showMonth = monthLabel !== lastMonth;
                    if (showMonth) lastMonth = monthLabel;

                    return (
                        <div key={idx} ref={todayDay ? todayRef : undefined}>
                            {showMonth && (
                                <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-4 py-2 -mx-1">
                                    <span className="text-sm font-bold text-[#1B365D] capitalize">{monthLabel}</span>
                                </div>
                            )}
                            <div className={`flex py-3 ${todayDay ? 'bg-[#1B365D]/[0.03]' : ''} border-b border-gray-100`}>
                                {/* Day column */}
                                <div className="w-14 shrink-0 text-center pt-1">
                                    <div className={`text-xs font-medium ${todayDay ? 'text-[#1B365D]' : 'text-gray-400'}`}>{dayOfWeek}</div>
                                    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-lg font-bold ${todayDay ? 'bg-[#1B365D] text-white' : 'text-[#1B365D]'}`}>
                                        {format(day, 'd')}
                                    </div>
                                </div>
                                {/* Events column */}
                                <div className="flex-1 space-y-1.5 min-h-[44px]">
                                    {dayEvents.length === 0 && (
                                        <div className="h-[44px]" onClick={() => openCreate(day)} />
                                    )}
                                    {dayEvents.map(ev => {
                                        const cancelled = ev.visit_status === 'annulee';
                                        const color = getEventColor(ev);
                                        const title = ev.prospect ? ev.prospect.nom : ev.title;
                                        const hasTime = ev.start_time && ev.end_time;
                                        return (
                                            <div
                                                key={ev.id}
                                                onClick={(e) => openDetail(ev, e)}
                                                data-testid={`mobile-event-${ev.id}`}
                                                className={`${color} text-white rounded-lg px-3 py-2.5 cursor-pointer active:opacity-80 transition-opacity ${cancelled ? 'opacity-70' : ''}`}
                                            >
                                                <div className={`text-sm font-medium leading-tight truncate ${cancelled ? 'line-through' : ''}`}>
                                                    {title}
                                                </div>
                                                {hasTime && (
                                                    <div className="text-xs text-white/80 mt-0.5 truncate">
                                                        {ev.start_time} - {ev.end_time}
                                                        {ev.location && ` (lieu : ${ev.location})`}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    /* =================== MAIN RENDER =================== */
    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="h-[calc(100vh-120px)] flex flex-col">
                {/* Header */}
                {isMobile ? (
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                        <h1 className="text-lg font-bold text-[#1B365D] capitalize" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="agenda-title">
                            Agenda
                        </h1>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => { if (todayRef.current) todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="h-8 px-3 text-xs" data-testid="nav-today">Aujourd'hui</Button>
                            <Button onClick={() => openCreate(new Date())}
                                disabled={!canWriteAgenda}
                                title={!canWriteAgenda ? 'Accès refusé : permission Écriture requise' : 'Nouveau'}
                                className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1 h-8 text-xs px-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="create-event-button">
                                <Plus className="w-4 h-4" /> Nouveau
                            </Button>
                        </div>
                    </div>
                ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 flex-shrink-0">
                    <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                        <h1 className="text-lg md:text-2xl font-bold text-[#1B365D] capitalize whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="agenda-title">
                            {viewTitle()}
                        </h1>
                        <div className="flex items-center gap-1">
                            <Button variant="outline" size="icon" onClick={() => nav('prev')} className="h-7 w-7 md:h-8 md:w-8" data-testid="nav-prev"><ChevronLeft className="h-4 w-4" /></Button>
                            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="h-7 md:h-8 px-2 md:px-3 text-xs md:text-sm" data-testid="nav-today">Aujourd'hui</Button>
                            <Button variant="outline" size="icon" onClick={() => nav('next')} className="h-7 w-7 md:h-8 md:w-8" data-testid="nav-next"><ChevronRight className="h-4 w-4" /></Button>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex bg-gray-100 rounded-lg p-0.5">
                            {VIEWS.map(v => {
                                const Icon = v.icon;
                                return (
                                    <button key={v.value} onClick={() => setView(v.value)} data-testid={`view-${v.value}`}
                                        className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-md text-xs md:text-sm font-medium transition-all ${view === v.value ? 'bg-[#1B365D] text-white shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}>
                                        <Icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{v.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <Button onClick={() => openCreate(currentDate)}
                            disabled={!canWriteAgenda}
                            title={!canWriteAgenda ? 'Accès refusé : permission Écriture requise' : 'Nouvel événement'}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1 h-8 md:h-9 text-xs md:text-sm px-2 md:px-4 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="create-event-button">
                            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nouvel événement</span><span className="sm:hidden">Nouveau</span>
                        </Button>
                    </div>
                </div>
                )}

                {isMobile ? renderMobileScheduleView() : (
                    <>
                    {view === 'month' && renderMonthView()}
                    {view === 'week' && renderWeekView()}
                    {view === 'day' && renderDayView()}
                    {view === 'list' && renderMobileScheduleView()}
                    </>
                )}

                {/* Legend - desktop only */}
                {!isMobile && (
                <div className="flex items-center gap-2 md:gap-4 mt-3 flex-shrink-0 flex-wrap">
                    <span className="text-xs md:text-sm text-gray-500">Légende:</span>
                    {EVENT_TYPES.map(t => (
                        <div key={t.value} className="flex items-center gap-1">
                            <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded ${t.color}`} />
                            <span className="text-[10px] md:text-sm text-gray-600">{t.label}</span>
                        </div>
                    ))}
                </div>
                )}
            </div>

            {/* =================== CREATE / EDIT MODAL =================== */}
            <Dialog open={showEventModal} onOpenChange={(open) => {
                if (!open) { confirmNavigation(closeEventModal); }
            }}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">Nouvel événement</DialogTitle>
                        <DialogDescription>{eventForm.date && format(parseISO(eventForm.date), 'EEEE d MMMM yyyy', { locale: fr })}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre *</Label>
                                <Input id="title" value={eventForm.title} onChange={(e) => updateEventForm({ title: e.target.value })} placeholder="Titre de l'événement" data-testid="event-title-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select value={eventForm.type} onValueChange={(v) => updateEventForm({ type: v })}>
                                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {EVENT_TYPES.map(t => (
                                            <SelectItem key={t.value} value={t.value}>
                                                <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded ${t.color}`} />{t.label}</div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Prospect lié</Label>
                                <Select value={eventForm.prospect_id || '_none'} onValueChange={(v) => {
                                    const pid = v === '_none' ? '' : v;
                                    const updates = { ...eventForm, prospect_id: pid };
                                    if (pid) {
                                        const pr = prospects.find(p => p.id === pid);
                                        if (pr) {
                                            const typeLabel = eventForm.type === 'visite_virtuelle' ? 'Visite virtuelle' : 'Visite présentiel';
                                            updates.title = `${typeLabel} - ${pr.nom}`;
                                            if (pr.adresse_depart?.adresse) updates.location = pr.adresse_depart.adresse;
                                            if (pr.email) {
                                                setProspectEmail(pr.email);
                                                setSendConfirmEmail(true);
                                            } else {
                                                setProspectEmail('');
                                                setSendConfirmEmail(false);
                                            }
                                        }
                                    } else {
                                        setProspectEmail('');
                                        setSendConfirmEmail(false);
                                    }
                                    setEventForm(updates);
                                    setHasChanges(true);
                                }}>
                                    <SelectTrigger data-testid="event-prospect-select"><SelectValue placeholder="Sélectionner un prospect" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">— Aucun prospect —</SelectItem>
                                        {prospects.map(pr => (
                                            <SelectItem key={pr.id} value={pr.id}>{pr.nom} {pr.telephone ? `- ${pr.telephone}` : ''}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Heure début</Label>
                                    <Select
                                        value={eventForm.start_time}
                                        onValueChange={(v) => {
                                            // Default to a 1h slot: end_time = start_time + 1h.
                                            updateEventForm({ start_time: v, end_time: addMinutes(v, 60) });
                                        }}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Heure fin</Label>
                                    <Select value={eventForm.end_time} onValueChange={(v) => updateEventForm({ end_time: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {canAssign && (
                                <div className="space-y-2">
                                    <Label>Assigné à</Label>
                                    <Select value={eventForm.assigned_to || '_none'} onValueChange={(v) => updateEventForm({ assigned_to: v === '_none' ? '' : v })}>
                                        <SelectTrigger data-testid="event-assign-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">— Non assigné —</SelectItem>
                                            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-xs text-gray-400">({u.role})</span></SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="location">Lieu</Label>
                                <Input id="location" value={eventForm.location} onChange={(e) => updateEventForm({ location: e.target.value })} placeholder="Adresse ou lieu" data-testid="event-location-input" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea id="description" value={eventForm.description} onChange={(e) => updateEventForm({ description: e.target.value })} placeholder="Description" rows={3} data-testid="event-description-input" />
                            </div>

                            {/* Email confirmation checkbox */}
                            {prospectEmail && (
                                <div className="flex items-start gap-3 p-3 rounded-lg border border-[#C9A962]/30 bg-[#C9A962]/5" data-testid="email-confirm-section">
                                    <input
                                        type="checkbox"
                                        id="sendConfirmEmail"
                                        checked={sendConfirmEmail}
                                        onChange={(e) => setSendConfirmEmail(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#C9A962] focus:ring-[#C9A962] cursor-pointer"
                                        data-testid="send-email-checkbox"
                                    />
                                    <label htmlFor="sendConfirmEmail" className="text-sm cursor-pointer select-none">
                                        <span className="font-medium text-[#1B365D]">Envoyer un email de confirmation</span>
                                        <br />
                                        <span className="text-xs text-gray-500">
                                            <Mail className="w-3 h-3 inline mr-1" />{prospectEmail}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => confirmNavigation(closeEventModal)}>Annuler</Button>
                            <Button type="submit" disabled={submitting} className="bg-[#1B365D] hover:bg-[#2A4A7A]" data-testid="event-submit-button">
                                {submitting ? 'Création...' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <UnsavedDialog />
        </Layout>
    );
}
